'use client';

import type { ProductFile } from '@eliadmin/core/src/api/products/types/product-types';
import type { ReactNode } from 'react';

import { FileTypeIcon } from '@eliadmin/core/src/components/dlz/product-detail/details/file-type-icon';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { phoneHref } from '@eliadmin/core/src/utils/dlz/text';
import { youtubeEmbedToNotEmbed } from '@eliadmin/core/src/utils/misc-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/** Texts of the tab that have no dictionary key (the dictionary has „Stiahnúť“, „Prehrať video“ …). */
const COPY = {
  title: 'Dokumenty na stiahnutie',
  helpTitle: 'Chýba vám dokument?',
  helpText: 'Certifikát, vyhlásenie o zhode alebo starší návod vám radi pošleme e-mailom.',
  writeEmail: 'Napísať e-mail',
};

/** Slovak plural of „súbor“: 1 súbor, 2 – 4 súbory, otherwise súborov. */
const filesLabel = (count: number) =>
  `${count} ${count === 1 ? 'súbor' : count < 5 ? 'súbory' : 'súborov'}`;

const extensionOf = (name: string) => /\.([a-z0-9]+)$/i.exec(name)?.[1].toLowerCase() ?? '';

/** Title of the admin media, else its file name without the extension. */
const titleOf = (file: ProductFile) =>
  file.custom_title?.trim() || file.name.replace(/\.[a-z0-9]+$/i, '');

const ACTION =
  'dlz-body-m-medium -m-2.5 inline-flex shrink-0 items-center gap-2 rounded-full p-2.5 text-[#D7191F] transition-colors hover:text-[#B5141A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012] md:m-0 md:p-0';

function DownloadRow({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  /** The action link. */
  children: ReactNode;
}) {
  return (
    <li className="flex items-center gap-4 border-b border-[#E2E4E7] p-4 last:border-b-0 md:gap-5 md:px-6 md:py-5">
      <span className="flex size-11 shrink-0 items-center justify-center rounded-[12px] bg-[#F5F6F7] md:size-[46px]">
        {icon}
      </span>
      <p className="dlz-body-m-medium min-w-0 flex-1 text-[#0F1012] [overflow-wrap:anywhere]">
        {title}
      </p>
      {children}
    </li>
  );
}

function ContactCard() {
  const { t } = useTranslations();
  const contact = useGlobalStore(s => s.generalLayout.contact);
  const phone = contact?.phone?.trim();
  const email = contact?.email?.trim();
  if (!phone && !email) return null;

  const items = [
    phone && {
      icon: 'phone-call' as const,
      label: t('text800'),
      value: phone,
      action: t('text1333'),
      href: phoneHref(phone),
    },
    email && {
      icon: 'at' as const,
      label: t('text569'),
      value: email,
      action: COPY.writeEmail,
      href: `mailto:${email}`,
    },
  ].filter(item => !!item);

  return (
    <aside className="hidden flex-col gap-4 self-start rounded-[18px] border border-[#E2E4E7] bg-white p-4 md:flex md:p-6">
      <div className="flex flex-col gap-2">
        <h3 className="dlz-h5 text-[#0F1012]">{COPY.helpTitle}</h3>
        <p className="dlz-body-s text-[#4F545C]">{COPY.helpText}</p>
      </div>
      <ul className="flex flex-col gap-4">
        {items.map(item => (
          <li key={item.icon} className="flex items-center gap-4">
            <Icon name={item.icon} size={40} className="text-[#D7191F]" />
            <div className="flex min-w-0 flex-col gap-1">
              <span className="dlz-body-m text-[#6B7079]">{item.label}</span>
              <span className="dlz-h5 text-[#0F1012] [overflow-wrap:anywhere]">{item.value}</span>
              <a
                href={item.href}
                aria-label={`${item.action} ${item.value}`}
                className="dlz-body-m-medium inline-flex items-center gap-1 self-start rounded-sm text-[#D7191F] transition-colors hover:text-[#B5141A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012]"
              >
                {item.action}
                <Icon name="caret-right" size={20} />
              </a>
            </div>
          </li>
        ))}
      </ul>
    </aside>
  );
}

/**
 * Figma „Tab · Na stiahnutie“ (60145:11460): the files of the product (type icon, title,
 * „Stiahnúť“) and the video as the last row, with the „Chýba vám dokument?“ contact card beside.
 * Phones (60459:107885) show the list only, the action as a 24 px icon; the advice box under the
 * tabs carries the same contacts there.
 * The card „Všetky dokumenty naraz (ZIP)“ of the design needs a backend archive and is not built,
 * and the file sizes of the design are not available: the public product projection drops the
 * asset metadata.
 */
export function ProductDownloads() {
  const { t } = useTranslations();
  const product = useVariantsStore(s => s.product);
  const videoEnabled = useGlobalStore(s => s.templateSettings.product_detail?.video?.enabled);
  const files = product.attachments ?? [];
  const video = videoEnabled ? product.video : null;
  const hasContact = !!useGlobalStore(
    s => s.generalLayout.contact?.phone?.trim() || s.generalLayout.contact?.email?.trim(),
  );

  return (
    <div
      className={cn(
        'grid gap-6',
        hasContact ? 'lg:grid-cols-[minmax(0,877px)_400px]' : 'lg:grid-cols-[minmax(0,877px)]',
      )}
    >
      <section aria-labelledby="product-downloads-title" className="flex min-w-0 flex-col gap-6">
        <div className="flex flex-col gap-2 md:flex-row md:items-baseline md:gap-3">
          <h2 id="product-downloads-title" className="dlz-h3 text-[#0F1012]">
            {COPY.title}
          </h2>
          {files.length ? (
            <p className="dlz-body-s text-[#6B7079]">{filesLabel(files.length)}</p>
          ) : null}
        </div>
        <ul className="overflow-hidden rounded-[18px] border border-[#E2E4E7]">
          {files.map(file => {
            const title = titleOf(file);
            return (
              <DownloadRow
                key={file.id}
                icon={<FileTypeIcon extension={extensionOf(file.name)} />}
                title={title}
              >
                <a
                  href={file.url}
                  download={file.name}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${t('text717')}: ${title}`}
                  className={ACTION}
                >
                  <Icon name="download" size={24} className="md:size-5" />
                  <span className="hidden md:inline">{t('text717')}</span>
                </a>
              </DownloadRow>
            );
          })}
          {video ? (
            <DownloadRow
              icon={<Icon name="play-circle" size={24} className="text-[#D7191F]" />}
              title={`${t('text451')}: ${product.name}`}
            >
              <a
                href={youtubeEmbedToNotEmbed(video)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${t('text2335')}: ${product.name}`}
                className={ACTION}
              >
                <Icon name="play-circle" size={24} className="md:size-5" />
                <span className="hidden md:inline">{t('text2335')}</span>
              </a>
            </DownloadRow>
          ) : null}
        </ul>
      </section>
      {hasContact ? <ContactCard /> : null}
    </div>
  );
}
